// src/components/OnboardingForm.jsx

import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../config/firebaseConfig';
import StepIndicator from './StepIndicator';
import LanguageSelector from './LanguageSelector';

const initialFormData = {
  accountType: '',
  corretorType: '',
  companyName: '',
  name: '',
  cpf: '',
  cnpj: '',
  phone: '',
  creci: '',
  city: '',
  website: '',
  email: '',
};

const OnboardingForm = ({ user, onComplete, setCurrentStep }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [step, setStep] = useState(() => parseInt(localStorage.getItem('onboardingStep')) || 1);
  const [formData, setFormData] = useState(() => JSON.parse(localStorage.getItem('onboardingFormData')) || initialFormData);
  const [errors, setErrors] = useState({});
  const [websiteConflict, setWebsiteConflict] = useState(false);

  useEffect(() => {
    localStorage.setItem('onboardingStep', step);
    setCurrentStep(step);
  }, [step, setCurrentStep]);

  useEffect(() => {
    localStorage.setItem('onboardingFormData', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    if (step === 2 && !formData.accountType) setStep(1);
  }, [formData.accountType, step]);

  useEffect(() => {
    const checkUserData = async () => {
      if (user?.uid) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          localStorage.removeItem('onboardingFormData');
          localStorage.removeItem('onboardingStep');
          setStep(1);
        }
      }
    };
    checkUserData();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'cpf') {
      newValue = value.replace(/\D/g, '').slice(0, 11)
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d{2})$/, '.$1-$2');
    }

    if (name === 'cnpj') {
      newValue = value.replace(/\D/g, '').slice(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d{2})$/, '$1-$2');
    }

    if (name === 'phone') {
      newValue = value.replace(/\D/g, '').slice(0, 11)
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d{4})$/, '$1-$2');
    }

    if (name === 'creci') {
      newValue = value.toUpperCase();
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (name === 'website') setWebsiteConflict(false);
  };

  const handleSave = async () => {
    setAttemptedSubmit(true);
  
    // Validação básica
    const requiredFields = ['accountType', 'name', 'phone', 'city', 'website', 'email'];
    if (formData.accountType === 'corretor' && formData.corretorType === 'pf') {
      requiredFields.push('cpf', 'creci');
    } else if (formData.accountType === 'corretor' && formData.corretorType === 'pj') {
      requiredFields.push('cnpj', 'creci');
    } else {
      requiredFields.push('companyName', 'cnpj', 'creci');
    }
  
    const isValid = requiredFields.every((field) => formData[field].trim() !== '');
    if (!isValid) return;
  
    try {
      // Verifica se o website já está em uso por outro usuário
      const websiteQuery = query(
        collection(db, 'users'),
        where('website', '==', formData.website)
      );
      const websiteSnap = await getDocs(websiteQuery);
  
      const websiteAlreadyUsedByAnother =
        !websiteSnap.empty && websiteSnap.docs.some((doc) => doc.id !== user.uid);
  
      if (websiteAlreadyUsedByAnother) {
        setWebsiteConflict(true);
        return;
      }
  
      const userRef = doc(db, 'users', user.uid);
  
      // Salva os dados do formulário no Firestore com merge
      await setDoc(
        userRef,
        {
          ...formData,
          onboardingComplete: true,
          role: 'master',
        },
        { merge: true }
      );
  
      // Verifica se já existem imóveis para esse usuário
      const propertiesRef = collection(db, 'properties');
      const q = query(propertiesRef, where('companyId', '==', user.uid), limit(1));
      const snapshot = await getDocs(q);
  
      if (snapshot.empty) {
        navigate('/dashboard/properties?lang=pt');
      } else {
        navigate('/dashboard/chatbot-settings?lang=pt');
      }
    } catch (error) {
      console.error('Erro ao salvar os dados do onboarding:', error);
      // Pode exibir uma notificação ou mensagem se desejar
    }
  };

  const handleFinalRedirect = async () => {
    try {
      const propertiesRef = collection(db, 'properties');
      const q = query(propertiesRef, where('companyId', '==', user.uid), limit(1));
      const snapshot = await getDocs(q);
  
      if (snapshot.empty) {
        navigate('/dashboard/properties?lang=pt');
      } else {
        navigate('/dashboard/chatbot-settings?lang=pt');
      }
    } catch (error) {
      console.error('Erro ao redirecionar após onboarding:', error);
    }
  };  
  
  const renderStepContent = () => (
    <>
      {step === 1 && (
        <div className="step-header centered-step">
          <h2>{intl.formatMessage({ id: 'onboarding.supportTextTitle' })}</h2>
          <p>{intl.formatMessage({ id: 'onboarding.stepInfo' })}</p>
          <div className="account-type-options">
            {['corretor', 'imobiliaria', 'construtora', 'incorporadora'].map((type) => (
              <button
                key={type}
                className={`secondary-button ${formData.accountType === type ? 'active' : ''}`}
                onClick={() => {
                  setFormData((prev) => ({ ...prev, accountType: type }));
                  setStep(2);
                }}
              >
                {intl.formatMessage({ id: type })}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="step-form">
          <h2 className="step-subtitle">
            {intl.formatMessage({ id: 'onboarding.step2.subtitle' })}
          </h2>
          <StepIndicator currentStep={2} totalSteps={3} />
          <div className="form-grid">
            {['imobiliaria', 'construtora', 'incorporadora'].includes(formData.accountType) && (
              <div className="form-group full-width">
                <label>{intl.formatMessage({ id: 'form.companyName' })}</label>
                <input
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={intl.formatMessage({ id: 'placeholder.companyName' })}
                  className={errors.companyName ? 'input-error' : ''}
                />
              </div>
            )}

            <div className="form-group full-width">
              <label>{intl.formatMessage({ id: 'form.name' })}</label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={intl.formatMessage({ id: 'placeholder.name' })}
                className={errors.name ? 'input-error' : ''}
              />
            </div>

            {formData.accountType === 'corretor' && (
              <div className="form-group full-width">
                <label>{intl.formatMessage({ id: 'selectCorretorType' })}</label>
                <select
                  name="corretorType"
                  value={formData.corretorType}
                  onChange={handleChange}
                  className={errors.corretorType ? 'input-error' : ''}
                >
                  <option value="">{intl.formatMessage({ id: 'placeholder.selectOption' })}</option>
                  <option value="pf">{intl.formatMessage({ id: 'corretorType.pf' })}</option>
                  <option value="pj">{intl.formatMessage({ id: 'corretorType.pj' })}</option>
                </select>
              </div>
            )}

            {formData.corretorType === 'pf' && (
              <div className="form-group full-width">
                <label>CPF</label>
                <input
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleChange}
                  placeholder="000.000.000-00"
                  className={errors.cpf ? 'input-error' : ''}
                />
              </div>
            )}

            {(formData.corretorType === 'pj' ||
              ['imobiliaria', 'construtora', 'incorporadora'].includes(formData.accountType)) && (
              <div className="form-group full-width">
                <label>CNPJ</label>
                <input
                  name="cnpj"
                  value={formData.cnpj}
                  onChange={handleChange}
                  placeholder="00.000.000/0000-00"
                  className={errors.cnpj ? 'input-error' : ''}
                />
              </div>
            )}

            {['corretor', 'imobiliaria', 'incorporadora'].includes(formData.accountType) && (
              <div className="form-group full-width">
                <label>CRECI</label>
                <input
                  name="creci"
                  value={formData.creci}
                  onChange={handleChange}
                  placeholder="Ex: SP-12345-F"
                  className={errors.creci ? 'input-error' : ''}
                />
              </div>
            )}

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'form.phone' })}</label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={intl.formatMessage({ id: 'placeholder.phone' })}
                className={errors.phone ? 'input-error' : ''}
              />
            </div>

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'form.city' })}</label>
              <input
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder={intl.formatMessage({ id: 'placeholder.city' })}
                className={errors.city ? 'input-error' : ''}
              />
            </div>

            <div className="form-group full-width">
              <label>{intl.formatMessage({ id: 'form.website' })}</label>
              <input
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder={intl.formatMessage({ id: 'placeholder.website' })}
                className={errors.website || websiteConflict ? 'input-error' : ''}
              />
              <small className="form-hint">
                {intl.formatMessage({ id: 'form.websiteHint' })}
              </small>
              {websiteConflict && (
                <p className="error-text">
                  {intl.formatMessage({ id: 'form.error.websiteExists', defaultMessage: 'Este website já está vinculado a uma conta master. Solicite acesso ao administrador.' })}
                </p>
              )}
            </div>
          </div>

          <div className="form-actions sticky-buttons">
            <button type="button" onClick={() => setStep(1)}>
              ← {intl.formatMessage({ id: 'backToStart' })}
            </button>
            <button type="button" onClick={handleSave}>
              {intl.formatMessage({ id: 'continue' })}
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="centered-step">
          <StepIndicator currentStep={3} totalSteps={3} />
          <h2>{intl.formatMessage({ id: 'onboarding.nextStepsTitle' })}</h2>
          <p>{intl.formatMessage({ id: 'onboarding.nextStepsText' })}</p>
          <div className="button-center">
            <button onClick={handleFinalRedirect}>
              {intl.formatMessage({ id: 'onboarding.nextStepsButton' })}
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="onboarding-wrapper">
      <div className="onboarding-content">{renderStepContent()}</div>
    </div>
  );
};

export default OnboardingForm;
